import { useQuery } from '@tanstack/react-query';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import React, { useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  getDeclarationPdfSchema,
  getDocument,
  getDocumentDataSources,
  getPropertiesSchema,
} from 'services/api/dynamicFormsApi';
import { QUERY_KEYS } from 'services/api/constants';
import { Container } from 'styles/Styles.styled';
import DataSyncIndicator from './components/DataSyncIndicator';
import DynamicContentRenderer from './DynamicContentRenderer';
import { StoreContext } from './hooks/useDocumentStore';
import createDocumentStore from './store/createDocumentStore';

interface Params {
  docId: string;
}

const DynamicFormDeclarationPdf: React.FC<RouteComponentProps<Params>> = ({
  match: {
    params: { docId },
  },
}) => {
  const docQuery = useQuery({
    queryKey: ['DOC', docId],
    queryFn: () => getDocument(docId),
    throwOnError: true,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const schemaId = docQuery.data?.schemaId;

  const propertiesSchemaQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PROPERTIES', schemaId],
    queryFn: () => getPropertiesSchema(schemaId!),
    enabled: !!docQuery.data,
    throwOnError: true,
  });
  const declarationPdfSchemaQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'DECLARATION_PDF', schemaId],
    queryFn: () => getDeclarationPdfSchema(schemaId!),
    enabled: !!docQuery.data,
    throwOnError: true,
  });
  const dataSourcesQuery = useQuery({
    queryKey: QUERY_KEYS.DYNAMIC_FORMS_DATA_SOURCES(docId, schemaId),
    queryFn: () => getDocumentDataSources(schemaId!, docId),
    enabled: !!docQuery.data,
    throwOnError: true,
  });

  const storeDeps = [docQuery.data, propertiesSchemaQuery.data, declarationPdfSchemaQuery.data, dataSourcesQuery.data];
  const store = useMemo(() => {
    const allLoaded = storeDeps.every(Boolean);
    if (!allLoaded) {
      return null;
    }

    return createDocumentStore({
      docId,
      initialData: docQuery.data!.data,
      initialSchemaId: docQuery.data!.schemaId,
      initialDataSources: dataSourcesQuery.data,
      state: docQuery.data!.state,
      metadata: docQuery.data!.metadata,
      properties: propertiesSchemaQuery.data,
    });
  }, storeDeps);

  if (!store) {
    return <Container>Loading...</Container>;
  }

  return (
    <StoreContext.Provider value={store}>
      <DataSyncIndicator />
      <DynamicContentRenderer schema={declarationPdfSchemaQuery.data!} />
    </StoreContext.Provider>
  );
};

export default withGreyBackground(DynamicFormDeclarationPdf);
